import type { PortalSubscriptionProvider } from "@/types/center";
import type { StaffRole } from "@/types/auth";

const DAY_MS = 24 * 60 * 60 * 1000;

// Manual-billing gyms must pay before access ends; recurring gyms are charged automatically.
const REMINDER_PROVIDERS: ReadonlySet<PortalSubscriptionProvider> = new Set([
  "flutterwave_manual",
  "manual_admin",
]);

export type RenewalStage = "upcoming" | "urgent" | "final";

// payer: can renew from Settings. staff: same gym, but can't pay.
// branch: billed through a parent center, so nobody here can pay.
export type RenewalAudience = "payer" | "staff" | "branch";

export interface RenewalTiming {
  stage: RenewalStage;
  daysLeft: number;
  expiresAt: Date;
}

export const isRenewalReminderProvider = (
  provider: PortalSubscriptionProvider | null | undefined,
) => Boolean(provider && REMINDER_PROVIDERS.has(provider));

export const getRenewalTiming = (
  nextBillingDate: string | null,
  now: Date,
): RenewalTiming | null => {
  if (!nextBillingDate) return null;

  const expiresAt = new Date(nextBillingDate);
  if (Number.isNaN(expiresAt.getTime())) return null;

  const msLeft = expiresAt.getTime() - now.getTime();
  if (msLeft <= 0 || msLeft > 7 * DAY_MS) return null;

  const stage: RenewalStage =
    msLeft <= DAY_MS ? "final" : msLeft <= 3 * DAY_MS ? "urgent" : "upcoming";

  return { stage, daysLeft: Math.ceil(msLeft / DAY_MS), expiresAt };
};

export const getRenewalAudience = ({
  role,
  isBillingOwner,
}: {
  role: StaffRole | null | undefined;
  isBillingOwner: boolean;
}): RenewalAudience => {
  if (!isBillingOwner) return "branch";
  return role === "Admin" ? "payer" : "staff";
};

const resolveTimeZone = (timezone: string | null | undefined) => {
  if (!timezone) return "UTC";
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone });
    return timezone;
  } catch {
    return "UTC";
  }
};

export const formatRenewalDate = (
  date: Date,
  timezone: string | null | undefined,
) =>
  new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: resolveTimeZone(timezone),
  }).format(date);

// Calendar day in the gym's timezone, so "hide until tomorrow" follows the gym's midnight.
export const getGymDayKey = (now: Date, timezone: string | null | undefined) =>
  new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: resolveTimeZone(timezone),
  }).format(now);

// Access ends at the time of the last payment, not midnight, so the final
// 24 hours can end "today" or "tomorrow" in the gym's timezone.
export const formatFinalDayEnd = (
  expiresAt: Date,
  now: Date,
  timezone: string | null | undefined,
) =>
  getGymDayKey(expiresAt, timezone) === getGymDayKey(now, timezone)
    ? "today"
    : "tomorrow";

export const buildRenewalShareMessage = ({
  gymName,
  expiryLabel,
  billedThroughCenterName,
  audience,
}: {
  gymName: string;
  expiryLabel: string;
  billedThroughCenterName: string;
  audience: RenewalAudience;
}) => {
  const subscription =
    audience === "branch" && billedThroughCenterName
      ? `the FirstRep portal subscription for ${gymName || "our gym"} (billed through ${billedThroughCenterName})`
      : `our FirstRep portal subscription${gymName ? ` for ${gymName}` : ""}`;

  return `Hi, ${subscription} ends ${expiryLabel}. Please renew before then to prevent interrupted access. Paying early is fine: the new period still starts from ${expiryLabel}. Renew in Settings → FirstRep Billing.`;
};

interface ReminderKeyParts {
  userId: string;
  gymId: string;
  nextBillingDate: string;
}

const reminderKey = (
  kind: "banner" | "modal" | "final",
  { userId, gymId, nextBillingDate }: ReminderKeyParts,
) => `renewal-reminder:${kind}:${userId}:${gymId}:${nextBillingDate}`;

const readStorage = (storage: () => Storage, key: string) => {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (storage: () => Storage, key: string, value: string) => {
  try {
    storage().setItem(key, value);
  } catch {
    // Storage blocked: the reminder simply shows again, which is the safe failure.
  }
};

const local = () => window.localStorage;
const session = () => window.sessionStorage;

export const isDismissedForDay = (
  kind: "banner" | "modal",
  parts: ReminderKeyParts,
  dayKey: string,
) => readStorage(local, reminderKey(kind, parts)) === dayKey;

export const dismissForDay = (
  kind: "banner" | "modal",
  parts: ReminderKeyParts,
  dayKey: string,
) => writeStorage(local, reminderKey(kind, parts), dayKey);

export const isFinalDayDismissed = (parts: ReminderKeyParts) =>
  readStorage(session, reminderKey("final", parts)) === "1";

export const dismissFinalDay = (parts: ReminderKeyParts) =>
  writeStorage(session, reminderKey("final", parts), "1");
